import { Box, Typography, Grid, Container } from '@mui/material';
import { Shield, CheckCircle2, TrendingUp, Users, Award, Clock, Target, FileCheck } from 'lucide-react';
import { useEffect } from 'react';
import "aos/dist/aos.css";
import AOS from "aos";

function Leadership() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      offset: 100,
      easing: 'ease-out-cubic',
    });
  }, []);

  const keyPrinciples = [
    { icon: Target, title: "Structured Planning", desc: "Comprehensive project roadmaps" },
    { icon: Shield, title: "Risk Management", desc: "Proactive issue identification" },
    { icon: FileCheck, title: "Compliance Oversight", desc: "Full regulatory adherence" },
    { icon: Clock, title: "Disciplined Scheduling", desc: "On-time project delivery" }
  ];

  const clientBenefits = [
    { icon: Users, title: "Clear Executive-Level Accountability" },
    { icon: CheckCircle2, title: "Structured Reporting & Communication" },
    { icon: Clock, title: "Strict Adherence to Build Schedules" },
    { icon: TrendingUp, title: "Controlled Cost Management" },
    { icon: Shield, title: "Professional Site Supervision" }
  ];

  const directorOversight = [
    "Supplier coordination",
    "Quality assurance processes",
    "Milestone approvals",
    "Regulatory compliance"
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        mt: { xs: 5, md: 5 },
        py: { xs: 6, md: 0 },
        background: 'linear-gradient(180deg, #ffffff 0%, var(--secondary-color) 100%)',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="xl" sx={{ overflow: 'hidden' }}>
        
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 5, md: 8 }} data-aos="fade-down">
          <Typography
            className="text-second fw-bold"
            sx={{
              fontSize: { xs: '36px', md: '48px' },
              mb: 2,
              letterSpacing: 1
            }}
          >
            LEADERSHIP & MANAGEMENT
          </Typography>
          
          <Box
            sx={{
              width: '100px',
              height: '4px',
              backgroundColor: 'var(--primary-text-color)',
              margin: '0 auto 24px'
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: '18px', md: '22px' },
              color: 'var(--black-color)',
              maxWidth: '900px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            Clarence Homes is led by an experienced Project Manager with active involvement in residential construction since 2019
          </Typography>
        </Box>

        {/* Main Content - Two Column Layout */}
        <Grid container spacing={{ xs: 4, md: 5 }} sx={{ mb: { xs: 6, md: 8 } }}>
          
          {/* Left Column - Professional Foundation */}
          <Grid item xs={12}>
            <Box data-aos="fade-right">
              <Typography
                className="text-second fw-bold"
                sx={{
                  fontSize: { xs: '26px', md: '32px' },
                  mb: 3
                }}
              >
                Professional Foundation
              </Typography>

              <Typography
                sx={{
                  fontSize: '16px',
                  color: 'var(--black-color)',
                  lineHeight: 1.8,
                  mb: 4
                }}
              >
                With a professional foundation in project management, our leadership applies structured planning, risk management, compliance oversight, and disciplined scheduling to every project undertaken. This ensures that each home is delivered with operational efficiency, regulatory compliance, and uncompromising quality standards.
              </Typography>

              {/* Key Principles Grid - 2x2 */}
              <Grid container spacing={2}>
                {keyPrinciples.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <Grid item xs={6} key={index}>
                      <Box
                        sx={{
                          backgroundColor: '#ffffff',
                          borderRadius: 2,
                          p: 2.5,
                          height: '100%',
                          border: '2px solid var(--secondary-color)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'var(--primary-text-color)',
                            transform: 'translateY(-6px)',
                            boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                            '& .principle-icon': {
                              backgroundColor: 'var(--primary-color)'
                            },
                            '& .principle-icon svg': {
                              color: 'var(--primary-text-color) !important'
                            }
                          }
                        }}
                      >
                        <Box
                          className="principle-icon"
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '12px',
                            backgroundColor: 'var(--secondary-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            transition: 'all 0.3s ease',
                            '& svg': {
                              color: 'var(--primary-color)',
                              transition: 'color 0.3s ease'
                            }
                          }}
                        >
                          <IconComponent size={24} />
                        </Box>
                        <Typography
                          className="fw-bold text-second"
                          sx={{ fontSize: '15px', mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: 'var(--black-light-color)'
                          }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Grid>

          {/* Right Column - Licensed NSW Builder */}
          <Grid item xs={12}>
            <Box
              data-aos="fade-left"
              sx={{
                backgroundColor: 'var(--primary-color)',
                borderRadius: 3,
                p: { xs: 4, md: 5 },
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-80px',
                  right: '-80px',
                  width: '250px',
                  height: '250px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(192,174,93,0.1)'
                }
              }}
            >
              <Box position="relative" zIndex={1}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary-text-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}
                >
                  <Award size={36} color="var(--primary-color)" />
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: '26px', md: '32px' },
                    fontWeight: 700,
                    color: 'var(--primary-text-color)',
                    mb: 2
                  }}
                >
                  Licensed NSW Builder
                </Typography>

                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#ffffff',
                    lineHeight: 1.8,
                    mb: 3
                  }}
                >
                  The Director of Clarence Homes maintains direct oversight of all construction activities:
                </Typography>

                <Grid container spacing={2}>
                  {directorOversight.map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <CheckCircle2 size={20} color="var(--primary-text-color)" style={{ flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: '15px',
                            color: '#ffffff',
                            fontWeight: 500
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Client Benefits Section */}
        <Box>
          <Box textAlign="center" mb={5} data-aos="fade-up">
            <Typography
              className="text-second fw-bold"
              sx={{
                fontSize: { xs: '32px', md: '40px' },
                mb: 2
              }}
            >
              Clients Benefit From
            </Typography>
            
            <Typography
              sx={{
                fontSize: '17px',
                color: 'var(--black-color)',
                maxWidth: '700px',
                margin: '0 auto'
              }}
            >
              Our leadership model ensures that every project is executed with precision and integrity
            </Typography>
          </Box>

          {/* Benefits Grid - 3 in first row, 2 in second row */}
          <Grid container spacing={3} justifyContent="center">
            {clientBenefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4}
                  key={index}
                  sx={{ maxWidth: { md: '350px' } }}
                >
                  <Box
                    data-aos="zoom-in"
                    data-aos-delay={index * 100}
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 3,
                      p: 3,
                      height: '100%',
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.06)',
                      border: '2px solid var(--secondary-color)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        backgroundColor: 'var(--primary-text-color)',
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease'
                      },
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                        borderColor: 'var(--primary-text-color)',
                        '&::before': {
                          transform: 'scaleX(1)'
                        },
                        '& .benefit-icon': {
                          backgroundColor: 'var(--primary-color)'
                        },
                        '& .benefit-icon svg': {
                          color: 'var(--primary-text-color) !important'
                        }
                      }
                    }}
                  >
                    <Box
                      className="benefit-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: 'var(--secondary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        transition: 'all 0.3s ease',
                        '& svg': {
                          color: 'var(--primary-color)',
                          transition: 'color 0.3s ease'
                        }
                      }}
                    >
                      <IconComponent size={32} />
                    </Box>
                    
                    <Typography
                      className="fw-bold text-second"
                      sx={{
                        fontSize: '15px',
                        lineHeight: 1.4
                      }}
                    >
                      {benefit.title}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Bottom Statement */}
          <Box
            data-aos="fade-up"
            sx={{
              mt: 6,
              backgroundColor: 'var(--primary-text-color)',
              borderRadius: 3,
              p: { xs: 4, md: 5 },
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(192,174,93,0.2)'
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '22px' },
                fontWeight: 700,
                color: 'var(--primary-color)',
                lineHeight: 1.6
              }}
            >
              "This leadership model ensures that every project is executed with precision and integrity."
            </Typography>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default Leadership;
